import { request } from './index'
import $store from '@/store/index'

/**
 * 申请 MQTT 连接凭据，用于实时 metrics 长连接 [Claude Code]
 * 来源 Apifox: POST /mqtt/getMqttToken (api-467021388)
 *
 * @param {Object} data - 请求参数（作为 query string 传递）
 * @param {string} data.processId - Process ID（必填），用于构建 subscribe topic
 *
 * @example
 * getMqttToken({ processId: 'proc-001' }).then(res => {
 *   if (res.errorCode === '0x0') {
 *     const { host, wsPort, clientId, username, token, subTopics } = res.result
 *   }
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',                                  // 0x0 = success
 *   errorInfo: 'success',                              // error message
 *   result: {
 *     token: '<jwt-token>',                            // 连接 token（用作 MQTT password）
 *     clientId: 'MediaHub_user@tvu.com_SID123',        // client id
 *     username: 'mqtt-user',                           // 用户名
 *     pubTopics: [],                                   // 发布主题列表
 *     subTopics: ['media/metrics/proc-001/frame/+'],   // 订阅主题列表
 *     host: 'gateway-dev.tvunetworks.com',             // MQTT 网关 host
 *     wsPort: 443                                      // WebSocket 端口
 *   }
 * }
 *
 * 失败响应示例：
 * {
 *   errorCode: '0x1',
 *   errorInfo: 'error message'
 * }
 */
export const getMqttToken = data =>
  request({ url: '/media-hub/mqtt/getMqttToken', method: 'POST', params: data })

/**
 * 更新 MQTT 订阅 topic（增量增订）[Claude Code]
 * 来源 Apifox: POST /mqtt/updateTopic (api-469279870, operationId=updateTopic)
 *
 * 用途：getMqttToken 只在首个 processId 建连时调用一次，token 通用于所有 processId；
 * 后续新增的 processId 不再 getMqttToken，改调本接口增订其 topic。
 *
 * @param {Object} data - 请求参数（JSON body）
 * @param {string[]} data.processIds - 流程 ID 列表（必填），每个后端拼成 media/metrics/{processId}/frame/+
 * @param {string} [data.mode] - 更新模式，例如 'add'（新增订阅）；留空默认为 'add'
 *
 * @example
 * updateMqttSubscribeTopics({ processIds: ['pid-ffff02', 'pid-ffff03'], mode: 'add' }).then(res => {
 *   if (res.errorCode === '0x0') {
 *     const subTopics = res.result.sub   // 更新后全部可订阅 topic 列表
 *   }
 * })
 *
 * @returns {Promise}
 * 成功响应示例：
 * {
 *   errorCode: '0x0',                                       // 业务码，0x0 表示成功
 *   errorInfo: 'Success',                                   // 业务提示信息
 *   result: {
 *     serviceName: 'MediaHub',                              // 服务名（MediaHub）
 *     clientId: 'MediaHub_user@tvunetworks.com_5b81982d',   // 客户端 ID（与 create 时一致）
 *     username: 'web-user',                                 // MQTT 用户名
 *     pub: [],                                              // 可发布的 topic 列表
 *     sub: [                                                // 更新后全部可订阅的 topic 列表
 *       'media/metrics/pid-ffff01/frame/+',
 *       'media/metrics/pid-ffff02/frame/+'
 *     ],
 *     issuedAtMs: 1780647492845                             // ACL 签发时间戳（毫秒）
 *   },
 *   errorResult: null,                                      // 错误详情（成功时为 null）
 *   repeated: null                                          // 重复标记（一般为 null）
 * }
 *
 * 失败响应示例：
 * {
 *   errorCode: '0x1',
 *   errorInfo: 'error message'
 * }
 */
export const updateMqttSubscribeTopics = data =>
  request({
    url: '/media-hub/mqtt/updateTopic',
    method: 'POST',
    disabledErrorTips: true,
    data
  })

// 注册/续期指标上报规则 [Claude Code]
// ⚠️ 依赖 initConfig.mqttGatewayPath —— mediahub 当前 initConfig 无此字段，待后端下发
export const mqttReportRule = ({ processId, rule }) =>
  request({
    url: `${$store.state.initConfig.mqttGatewayPath}/control/bridge/${processId}/metrics/report/rule`,
    method: 'POST',
    disabledErrorTips: true,
    data: rule
  })

// 拉取 metadata（Observer）[Claude Code]
// ⚠️ 依赖 initConfig.obServerPath —— mediahub 当前 initConfig 无此字段，待后端下发；URL 照搬 MA [Claude Code]
export const getInfoFromOb = data =>
  request({
    url: `${$store.state.initConfig.obServerPath}api/observer/v1/get`,
    method: 'POST',
    disabledErrorTips: true,
    data
  })

// 查询历史指标（Observer）[Claude Code]
// ⚠️ 依赖 initConfig.obServerPath —— mediahub 当前 initConfig 无此字段，待后端下发；URL 照搬 MA [Claude Code]
export const queryMetricsHistoryData = data =>
  request({
    url: `${$store.state.initConfig.obServerPath}api/observer/v1/queryMetricsHistoryData`,
    method: 'POST',
    disabledErrorTips: true,
    data
  })
